import type {
  ManagedAs,
  ManagedCalendar,
  ManagedCalendarItem,
  MyCalendar,
  SchedulerDays,
} from "@fieldpath/shared";
import { daysOverdue, mdy } from "@fieldpath/shared";
import { useEffect, useMemo, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { schedulerUrl } from "../config";
import { navigate } from "../router";
import { KindTag } from "./KindTag";
import { Modal } from "./Modal";

/**
 * THE CALENDARS (C.1, Jeremy 13 Oct 2026, from beta feedback; mockups
 * reviewed and approved the same day). Two pop-ups over the page that opened
 * them, so closing one puts you back where you were:
 *
 *   - **My calendar**, from My work: my stages coming up (on their due date),
 *     the ones I finished (on the day I finished them), HR's holidays and my
 *     own time off. Requesting time off stays in the Scheduler, linked.
 *   - **Work you manage**, from "You manage": client dates, task and stage
 *     due dates across everything I manage by any of the five ties, with
 *     toggles and ONE search across people, clients, projects, deliverables
 *     and tasks. Picked filters of one kind widen ("Priya or Dana"), of two
 *     kinds narrow ("Priya, at Linea").
 *
 * **A face, no rules.** What is mine, what I manage and what is late are the
 * server's answers (`db/calendar.ts`); this lays them out by day. The one
 * judgement made here is WHICH DAY a completion falls on: the server sends
 * the moment, and the reader's own timezone decides the day, as every other
 * time on screen is shown.
 *
 * **What the scheduler could not answer is said**, in the warning style,
 * never drawn as a calendar with no holidays or no time off on it.
 */

// ── Dates, as calendar days in the reader's timezone ───────────────────────

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const pad = (n: number) => String(n).padStart(2, "0");
/** A local Date as YYYY-MM-DD. */
const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** YYYY-MM-DD as a local Date, never through UTC, so the day cannot shift. */
const dateOf = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
};
const todayIso = () => isoOf(new Date());

interface Month {
  readonly y: number;
  readonly m: number;
}

/** Every day the month view shows, Sunday first, with the edges of the months either side. */
function cellsOf({ y, m }: Month): string[] {
  const lead = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const count = Math.ceil((lead + days) / 7) * 7;
  return Array.from({ length: count }, (_, i) => isoOf(new Date(y, m, 1 - lead + i)));
}

const step = ({ y, m }: Month, by: number): Month => {
  const d = new Date(y, m + by, 1);
  return { y: d.getFullYear(), m: d.getMonth() };
};
const thisMonth = (): Month => {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() };
};

// ── The parts both calendars share ─────────────────────────────────────────

/** How a chip on the grid reads: each tone is one CSS class. */
type Tone = "due" | "late" | "done" | "off" | "client" | "task" | "stage";

interface Chip {
  readonly key: string;
  readonly label: string;
  readonly tone: Tone;
}

/** Fetch a month's range; keep the last answer on screen while the next loads. */
function useMonth<T>(load: (from: string, through: string) => Promise<T>, month: Month) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cells = useMemo(() => cellsOf(month), [month]);
  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    let live = true;
    setError(null);
    load(cells[0]!, cells[cells.length - 1]!)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof ApiError ? e.message : String(e)));
    return () => {
      live = false;
    };
    // `load` is a fresh closure each render; the month is what changes the
    // answer, with coming back to the tab.
  }, [cells, back]);
  return { data, error, cells };
}

function MonthNav({ month, onMonth }: { month: Month; onMonth: (m: Month) => void }) {
  return (
    <div className="cal-nav">
      <button type="button" aria-label="Previous month" onClick={() => onMonth(step(month, -1))}>
        ‹
      </button>
      <span className="cal-month">
        {MONTHS[month.m]} {month.y}
      </span>
      <button type="button" aria-label="Next month" onClick={() => onMonth(step(month, 1))}>
        ›
      </button>
      <button type="button" className="cal-today" onClick={() => onMonth(thisMonth())}>
        Today
      </button>
    </div>
  );
}

function Toggle({ on, tone, label, onClick }: { on: boolean; tone: Tone | "holiday"; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`cal-toggle${on ? " on" : ""}`} aria-pressed={on} onClick={onClick}>
      <span className={`cal-swatch tone-${tone}`} aria-hidden="true" />
      {label}
    </button>
  );
}

/** The month itself. Each day is a button: it selects the day for the list beside. */
function MonthGrid({
  month,
  cells,
  selected,
  onSelect,
  dayOf,
}: {
  month: Month;
  cells: readonly string[];
  selected: string;
  onSelect: (iso: string) => void;
  dayOf: (iso: string) => { holiday: string | null; chips: readonly Chip[] };
}) {
  const today = todayIso();
  const weeks: string[][] = [];
  cells.forEach((iso, i) => (i % 7 === 0 ? weeks.push([iso]) : weeks[weeks.length - 1]!.push(iso)));
  return (
    <div className="cal-grid">
      <div className="cal-weekdays" aria-hidden="true">
        {WEEKDAYS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0]} className="cal-week">
          {week.map((iso) => {
            const d = dateOf(iso);
            const { holiday, chips } = dayOf(iso);
            const cls = [
              "cal-day",
              d.getMonth() !== month.m && "outside",
              (d.getDay() === 0 || d.getDay() === 6) && "weekend",
              holiday && "holiday",
              iso === selected && "selected",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                key={iso}
                type="button"
                className={cls}
                aria-pressed={iso === selected}
                aria-label={`${mdy(iso)}${holiday ? `, ${holiday}` : ""}, ${chips.length} item${chips.length === 1 ? "" : "s"}`}
                onClick={() => onSelect(iso)}
              >
                <span className="cal-day-head">
                  <span className={iso === today ? "cal-num today" : "cal-num"}>{d.getDate()}</span>
                  {holiday ? <span className="cal-holiday">{holiday}</span> : null}
                </span>
                {chips.slice(0, 3).map((c) => (
                  <span key={c.key} className={`cal-chip tone-${c.tone}`}>
                    {c.label}
                  </span>
                ))}
                {chips.length > 3 ? <span className="cal-more">+{chips.length - 3} more</span> : null}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** The selected day's heading in the side panel. */
function DayHead({ iso, holiday }: { iso: string; holiday: string | null }) {
  const d = dateOf(iso);
  return (
    <div className="cal-side-head">
      <div className="cal-side-weekday">{WEEKDAYS_LONG[d.getDay()]}</div>
      <div className="cal-side-date">{mdy(iso)}</div>
      {holiday ? <div className="cal-side-holiday">Company holiday: {holiday}</div> : null}
    </div>
  );
}

/** Holidays by date, or none when the list could not be read (said elsewhere). */
function holidayMap(h: SchedulerDays<{ date: string; name: string | null }> | undefined) {
  const out = new Map<string, string>();
  if (h?.available) for (const d of h.days) out.set(d.date, d.name ?? "Company holiday");
  return out;
}

/** The first letters of a name, for a chip. */
function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .slice(0, 2)
    .join("");
}

// ── My calendar ────────────────────────────────────────────────────────────

type MyLayer = "due" | "done" | "holidays" | "off";

interface MyEntry {
  readonly key: string;
  readonly day: string;
  readonly tone: "due" | "late" | "done" | "off";
  readonly chip: string;
  readonly badge: string;
  readonly title: string;
  readonly sub: string | null;
  readonly client: string | null;
  readonly taskId: string | null;
}

export function MyCalendarDialog({ fetcher, onClose }: { fetcher: Fetcher; onClose: () => void }) {
  const [month, setMonth] = useState<Month>(thisMonth);
  const [selected, setSelected] = useState(todayIso);
  // Done starts OFF, the toggle still there to show it (punch list #35,
  // Jeremy 20 Oct 2026): finished work crowded the month people open the
  // calendar to plan.
  const [show, setShow] = useState<Record<MyLayer, boolean>>({ due: true, done: false, holidays: true, off: true });
  const { data, error, cells } = useMonth<MyCalendar>((a, b) => api.myCalendar(fetcher, a, b), month);
  const today = todayIso();

  const entries = useMemo(() => {
    const out: MyEntry[] = [];
    for (const s of data?.stages ?? []) {
      const where = [s.deliverableName, s.projectName].filter(Boolean).join(" · ");
      if (s.completedAt !== null) {
        out.push({
          key: `done-${s.taskId}-${s.stageName}`,
          day: isoOf(new Date(s.completedAt)),
          tone: "done",
          chip: `✓ ${s.stageName} · ${s.taskTitle}`,
          badge: "Done · completed this day",
          title: `${s.stageName} · ${s.taskTitle}`,
          sub: where,
          client: s.clientName,
          taskId: s.taskId,
        });
      } else if (s.dueDate !== null) {
        // The one overdue rule (shared, F150), the task and project pages' too.
        const n = daysOverdue(s.dueDate, today) ?? 0;
        const late = n > 0;
        out.push({
          key: `due-${s.taskId}-${s.stageName}`,
          day: s.dueDate,
          tone: late ? "late" : "due",
          chip: `${s.stageName} · ${s.taskTitle}`,
          badge: late ? `Late · ${n} day${n === 1 ? "" : "s"}` : s.dueDate === today ? "Due today" : "Due",
          title: `${s.stageName} · ${s.taskTitle}`,
          sub: where,
          client: s.clientName,
          taskId: s.taskId,
        });
      }
    }
    if (data?.timeOff.available) {
      for (const d of data.timeOff.days) {
        out.push({
          key: `off-${d.date}`,
          day: d.date,
          tone: "off",
          chip: `Time off · ${d.hours} h`,
          badge: "Time off",
          title: d.hours >= 8 ? "Full day off" : `${d.hours} hours off`,
          sub: "Approved in the Scheduler",
          client: null,
          taskId: null,
        });
      }
    }
    return out;
  }, [data, today]);

  const visible = (e: MyEntry) => (e.tone === "done" ? show.done : e.tone === "off" ? show.off : show.due);
  const holidays = show.holidays ? holidayMap(data?.holidays) : new Map<string, string>();
  const on = (iso: string) => entries.filter((e) => e.day === iso && visible(e));
  const agenda = on(selected);
  const flip = (k: MyLayer) => setShow((s) => ({ ...s, [k]: !s[k] }));

  return (
    <Modal title="My calendar" onCancel={onClose} wide help="calendar">
      <div className="cal">
        <div className="cal-bar">
          <MonthNav month={month} onMonth={setMonth} />
          <div className="cal-toggles" role="group" aria-label="Show">
            <Toggle on={show.due} tone="due" label="Coming up" onClick={() => flip("due")} />
            <Toggle on={show.done} tone="done" label="Done" onClick={() => flip("done")} />
            <Toggle on={show.holidays} tone="holiday" label="Holidays" onClick={() => flip("holidays")} />
            <Toggle on={show.off} tone="off" label="My time off" onClick={() => flip("off")} />
          </div>
        </div>
        {error ? <div className="error">{error}</div> : null}
        {data && !data.holidays.available ? (
          <p className="cal-warn">Holidays could not be read from the Scheduler, so none are shown.</p>
        ) : null}
        {data && !data.timeOff.available ? (
          <p className="cal-warn">Your time off could not be read from the Scheduler, so none is shown.</p>
        ) : null}
        <div className="cal-body">
          <MonthGrid
            month={month}
            cells={cells}
            selected={selected}
            onSelect={setSelected}
            dayOf={(iso) => ({
              holiday: holidays.get(iso) ?? null,
              chips: on(iso).map((e) => ({ key: e.key, label: e.chip, tone: e.tone })),
            })}
          />
          <aside className="cal-side">
            <DayHead iso={selected} holiday={holidays.get(selected) ?? null} />
            <div className="cal-agenda">
              {agenda.length === 0 ? (
                <p className="muted">{data ? "Nothing on this day." : "Loading…"}</p>
              ) : (
                agenda.map((e) => (
                  <div key={e.key} className="cal-entry">
                    <span className={`cal-badge tone-${e.tone}`}>{e.badge}</span>
                    <span className="cal-entry-title">{e.title}</span>
                    {e.sub ? <span className="cal-entry-sub">{e.sub}</span> : null}
                    {e.client ? <span className="cal-entry-sub client-name">{e.client}</span> : null}
                    {e.taskId ? (
                      <button
                        type="button"
                        className="cal-open"
                        onClick={() => {
                          onClose();
                          navigate({ kind: "task", id: e.taskId! });
                        }}
                      >
                        Open task ›
                      </button>
                    ) : null}
                  </div>
                ))
              )}
            </div>
            <div className="cal-side-foot">
              <a href={schedulerUrl} target="_blank" rel="noreferrer">
                Request time off in Scheduler ↗
              </a>
              <span>Time off is read from the Scheduler; holidays are HR&apos;s list.</span>
            </div>
          </aside>
        </div>
        <p className="cal-note">Done work sits on the day the stage was completed; coming up and late work on its due date.</p>
      </div>
    </Modal>
  );
}

// ── Work you manage ────────────────────────────────────────────────────────

type ManagedLayer = "client" | "task" | "stage";

/** What the one search bar can filter on, and the field each reads. */
const FACETS = [
  { type: "Person", plural: "People", of: (i: ManagedCalendarItem) => i.people },
  { type: "Client", plural: "Clients", of: (i: ManagedCalendarItem) => (i.clientName ? [i.clientName] : []) },
  { type: "Project", plural: "Projects", of: (i: ManagedCalendarItem) => [i.projectName] },
  { type: "Deliverable", plural: "Deliverables", of: (i: ManagedCalendarItem) => (i.deliverableName ? [i.deliverableName] : []) },
  { type: "Task", plural: "Tasks", of: (i: ManagedCalendarItem) => (i.taskTitle ? [i.taskTitle] : []) },
] as const;
type FacetType = (typeof FACETS)[number]["type"];

interface Pick {
  readonly type: FacetType;
  readonly value: string;
}

const WHY: Record<ManagedAs, string> = {
  owner: "You: task owner",
  deliverable_pm: "You: deliverable PM",
  deliverable_tm: "You: deliverable TM",
  project_pm: "You: project PM",
  project_tm: "You: project TM",
};

const ORDER: Record<ManagedLayer, number> = { client: 0, task: 1, stage: 2 };

export function ManagedCalendarDialog({ fetcher, onClose }: { fetcher: Fetcher; onClose: () => void }) {
  const [month, setMonth] = useState<Month>(thisMonth);
  const [selected, setSelected] = useState(todayIso);
  // Stages start OFF: on a busy month they are most of the chips, and the
  // client and task dates are what a PM scans for first.
  const [show, setShow] = useState<Record<ManagedLayer, boolean>>({ client: true, task: true, stage: false });
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [picks, setPicks] = useState<readonly Pick[]>([]);
  const { data, error, cells } = useMonth<ManagedCalendar>((a, b) => api.managedCalendar(fetcher, a, b), month);
  const today = todayIso();
  const items = data?.items ?? [];

  const q = query.trim().toLowerCase();
  const matches = (i: ManagedCalendarItem) => {
    if (!show[i.kind]) return false;
    // Picks of ONE kind widen, picks of different kinds narrow.
    for (const f of FACETS) {
      const wanted = picks.filter((p) => p.type === f.type).map((p) => p.value);
      if (wanted.length > 0 && !f.of(i).some((v) => wanted.includes(v))) return false;
    }
    // Typed and not picked: anything, in any field.
    return q === "" || FACETS.some((f) => f.of(i).some((v) => v.toLowerCase().includes(q)));
  };
  const visible = items.filter(matches);

  const suggestions = useMemo(() => {
    if (q === "") return [];
    return FACETS.map((f) => {
      const counts = new Map<string, number>();
      for (const i of items) {
        for (const v of f.of(i)) {
          if (!v.toLowerCase().includes(q)) continue;
          if (picks.some((p) => p.type === f.type && p.value === v)) continue;
          counts.set(v, (counts.get(v) ?? 0) + 1);
        }
      }
      return { facet: f, options: [...counts].sort(([a], [b]) => a.localeCompare(b)).slice(0, 5) };
    }).filter((g) => g.options.length > 0);
  }, [q, items, picks]);

  const pick = (type: FacetType, value: string) => {
    setPicks((p) => [...p, { type, value }]);
    setQuery("");
    setOpen(false);
  };

  const holidays = holidayMap(data?.holidays);
  const chipOf = (i: ManagedCalendarItem): Chip => {
    const late = !i.done && i.kind !== "client" && i.date < today;
    const tone: Tone = i.done ? "done" : late || i.pastClientDate ? "late" : i.kind;
    // Who has it FIRST: the chip truncates, and who has it is what a PM scans for.
    const who = i.people.length > 0 ? `${i.people.map(initials).join(", ")} · ` : "";
    const label =
      i.kind === "client"
        ? `Client due · ${i.title}${i.clientName ? ` · ${i.clientName}` : ""}`
        : `${i.done ? "✓ " : ""}${who}${i.title}`;
    return { key: `${i.kind}-${i.title}-${i.date}-${i.taskId ?? i.deliverableId}`, label, tone };
  };
  const on = (iso: string) =>
    visible.filter((i) => i.date === iso).sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);
  const agenda = on(selected);

  const inMonth = (iso: string) => dateOf(iso).getMonth() === month.m;
  const shown = visible.filter((i) => inMonth(i.date)).length;
  const total = items.filter((i) => show[i.kind] && inMonth(i.date)).length;
  const filtering = picks.length > 0 || q !== "";
  const flip = (k: ManagedLayer) => setShow((s) => ({ ...s, [k]: !s[k] }));

  return (
    <Modal title="Work you manage" onCancel={onClose} wide help="calendar">
      <div className="cal">
        <p className="cal-intro">
          Everything where you are the project&apos;s PM or TM, the deliverable&apos;s PM or TM, or
          the task&apos;s owner.
        </p>
        <div className="cal-bar">
          <MonthNav month={month} onMonth={setMonth} />
          <div className="cal-toggles" role="group" aria-label="Show">
            <Toggle on={show.client} tone="client" label="Client dates" onClick={() => flip("client")} />
            <Toggle on={show.task} tone="task" label="Tasks" onClick={() => flip("task")} />
            <Toggle on={show.stage} tone="stage" label="Stages" onClick={() => flip("stage")} />
          </div>
        </div>

        <div className="cal-search">
          <div className="cal-search-box">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <circle cx="7" cy="7" r="4.5" />
              <path d="M10.5 10.5l3.5 3.5" />
            </svg>
            {picks.map((p) => (
              <span key={`${p.type}-${p.value}`} className="cal-pick">
                <span className="cal-pick-type">{p.type}</span>
                <span className={p.type === "Client" ? "client-name" : undefined}>{p.value}</span>
                <button
                  type="button"
                  aria-label={`Remove ${p.value}`}
                  onClick={() => setPicks((all) => all.filter((x) => x !== p))}
                >
                  ✕
                </button>
              </span>
            ))}
            <input
              aria-label="Search people, clients, projects, deliverables and tasks"
              placeholder="Search a person, client, project, deliverable or task"
              value={query}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                // Enter takes the first suggestion: the quickest way to a pick.
                const first = suggestions[0];
                if (e.key === "Enter" && first) {
                  e.preventDefault();
                  pick(first.facet.type, first.options[0]![0]);
                }
              }}
            />
          </div>
          {open && q !== "" ? (
            <div className="cal-suggest" role="listbox" aria-label="Matches">
              {suggestions.length === 0 ? (
                <div className="cal-suggest-none">No person, client, project, deliverable or task matches.</div>
              ) : (
                suggestions.map((g) => (
                  <div key={g.facet.type}>
                    <div className="cal-suggest-group">{g.facet.plural}</div>
                    {g.options.map(([value, count]) => (
                      <button
                        key={value}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="cal-suggest-option"
                        onClick={() => pick(g.facet.type, value)}
                      >
                        <span className={g.facet.type === "Client" ? "client-name" : undefined}>{value}</span>
                        <span className="muted">{count} on the calendar</span>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>

        <div className="cal-count">
          <span>
            {filtering
              ? `Showing ${shown} of ${total} dates in ${MONTHS[month.m]} that match`
              : `${total} date${total === 1 ? "" : "s"} in ${MONTHS[month.m]} across the work you manage`}
          </span>
          {filtering ? (
            <button
              type="button"
              className="linkish"
              onClick={() => {
                setPicks([]);
                setQuery("");
              }}
            >
              Clear search
            </button>
          ) : null}
        </div>

        {error ? <div className="error">{error}</div> : null}
        {data && !data.holidays.available ? (
          <p className="cal-warn">Holidays could not be read from the Scheduler, so none are shown.</p>
        ) : null}

        <div className="cal-body" onMouseDown={() => setOpen(false)}>
          <MonthGrid
            month={month}
            cells={cells}
            selected={selected}
            onSelect={setSelected}
            dayOf={(iso) => ({ holiday: holidays.get(iso) ?? null, chips: on(iso).map(chipOf) })}
          />
          <aside className="cal-side">
            <DayHead iso={selected} holiday={holidays.get(selected) ?? null} />
            <div className="cal-agenda">
              {agenda.length === 0 ? (
                <p className="muted">{data ? "Nothing you manage falls on this day." : "Loading…"}</p>
              ) : (
                agenda.map((i) => {
                  const n = i.kind === "client" ? 0 : (daysOverdue(i.date, today, i.done) ?? 0);
                  const late = n > 0;
                  const badge = i.done
                    ? "Done"
                    : late
                      ? `Late · ${n} day${n === 1 ? "" : "s"}`
                      : i.kind === "client"
                        ? "Due to client"
                        : i.kind === "task"
                          ? "Task due"
                          : "Stage due";
                  const tone = i.done ? "done" : late ? "late" : i.kind;
                  return (
                    <div key={chipOf(i).key} className="cal-entry">
                      <span className="cal-entry-top">
                        <span className={`cal-badge tone-${tone}`}>{badge}</span>
                        <span className="cal-why">{WHY[i.managedAs]}</span>
                      </span>
                      <span className="cal-entry-title">
                        {i.kind === "client" ? (
                          <KindTag kind="deliverable" size="row" />
                        ) : i.kind === "task" ? (
                          <KindTag kind="task" size="row" />
                        ) : (
                          <span className="cal-stage-tag">STG</span>
                        )}
                        {i.title}
                      </span>
                      <span className="cal-entry-sub">
                        {i.kind === "client"
                          ? i.projectName
                          : `${i.deliverableName ?? "Not part of a deliverable"} · ${i.projectName}`}
                      </span>
                      {i.clientName ? <span className="cal-entry-sub client-name">{i.clientName}</span> : null}
                      {i.people.length > 0 ? (
                        <span className="cal-entry-people">
                          {i.kind === "client" ? "PM: " : "With "}
                          {i.people.join(", ")}
                        </span>
                      ) : null}
                      {i.pastClientDate && !i.done ? (
                        <span className="cal-entry-warn">Due after the deliverable&apos;s client date.</span>
                      ) : null}
                      <button
                        type="button"
                        className="cal-open"
                        onClick={() => {
                          onClose();
                          navigate(
                            i.taskId
                              ? { kind: "task", id: i.taskId }
                              : { kind: "deliverable", id: i.deliverableId! },
                          );
                        }}
                      >
                        {i.taskId ? "Open task ›" : "Open deliverable ›"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </aside>
        </div>
      </div>
    </Modal>
  );
}
